import React, { useEffect, useState } from 'react'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { SubServiceCard } from '../components/SubServiceCard'
import { CTASection } from '../components/CTASection'
import { TShirtSizeGuide } from '../components/TShirtSizeGuide'
import { CapacityIndicator } from '../components/CapacityIndicator'
import { FeaturePipeline } from '../components/FeaturePipeline'
import { productBuildingServices } from '../data/mockData'
import { getPricingTiers, type PricingTier, type Currency } from '../services/cms'
import { loadCurrency, saveCurrency } from '../utils/currency'
import { motion } from 'framer-motion'
import { SeoHelmet } from '../components/SeoHelmet'
import { ROUTE_META } from '../data/routeMeta'

const currencies: { code: Currency; label: string }[] = [
  { code: 'EUR', label: 'EUR' },
  { code: 'USD', label: 'USD' },
  { code: 'SGD', label: 'SGD' },
  { code: 'GBP', label: 'GBP' },
]

export function ProductBuilding() {
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currency, setCurrency] = useState<Currency>(loadCurrency)

  useEffect(() => {
    getPricingTiers()
      .then(setPricingTiers)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load pricing'))
      .finally(() => setLoading(false))
  }, [])
  return (
    <div className="min-h-screen w-full bg-white flex flex-col font-['Roboto']">
      <SeoHelmet {...ROUTE_META['/product-building']} url="/product-building" pageType="website" />
      <Header />

      <main className="flex-1">
        <section className="relative w-full bg-navy py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
              alt="Code on screen"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-transparent" />
          </div>

          <div className="max-w-5xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white font-['Poppins']">
                Product building <span className="text-primary">with AI</span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed max-w-3xl">
                We build your digital product or MVP using AI-accelerated workflows, from idea to shippable.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12 text-center"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 font-['Poppins']">
                How we can help
              </h2>
            </motion.div>

            <div className="space-y-8">
              {productBuildingServices.map((service, index) => (
                <SubServiceCard
                  key={index}
                  title={service.title}
                  description={service.description}
                  icon={service.icon}
                  delay={service.delay}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16 text-center"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 font-['Poppins']">
                Transparent pricing
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto font-['Roboto']">
                We don't ship demos. Every feature goes from UX insight to
                production, and we keep building with you, month after month, on
                a simple flat retainer.
              </p>
            </motion.div>

            <FeaturePipeline />

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16 rounded-xl bg-light-background border border-gray-200 px-6 py-5 text-center"
            >
              <p className="text-lg sm:text-xl font-medium text-gray-900 font-['Poppins']">
                A demo looks done.{' '}
                <span className="text-primary">A product stays done.</span>
              </p>
            </motion.div>

            <div className="flex justify-center mb-10">
              <div
                role="group"
                aria-label="Select currency"
                className="inline-flex items-center bg-gray-100 rounded-full p-1"
              >
                {currencies.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => { setCurrency(c.code); saveCurrency(c.code) }}
                    aria-pressed={currency === c.code}
                    className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                      currency === c.code
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
              {loading ? (
                <div className="col-span-full flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
              ) : error ? (
                <div className="col-span-full bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl text-center">
                  {error}
                </div>
              ) : (
                pricingTiers.map((tier) => (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: tier.delay }}
                  className={`${
                    tier.onDark
                      ? 'bg-navy border-navy shadow-xl transform md:-translate-y-4'
                      : 'bg-white border-gray-200 shadow-sm hover:shadow-xl'
                  } rounded-2xl p-8 border relative flex flex-col h-full transition-all duration-300`}
                >
                  {tier.isPopular && (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className={`mb-6 ${tier.isPopular ? 'mt-2' : ''}`}>
                    <h3
                      className={`text-xl font-bold mb-2 font-['Poppins'] ${
                        tier.onDark ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      {tier.title}
                    </h3>
                    <p
                      className={`text-sm font-['Roboto'] h-10 ${
                        tier.onDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      {tier.subtitle}
                    </p>
                  </div>
                  <div className="mb-6">
                    <span
                      className={`text-4xl font-bold ${
                        tier.onDark ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      {tier.price[currency] ?? tier.price.EUR}
                    </span>
                    <span className={tier.onDark ? 'text-gray-400' : 'text-gray-500'}>
                      /mo
                    </span>
                  </div>
                  <div className="mb-6">
                    {tier.variant === 'time' ? (
                      <CapacityIndicator
                        variant="time"
                        tierColor={tier.tierColor}
                        timeLabel={tier.timeLabel!}
                        timeSub={tier.timeSub!}
                      />
                    ) : (
                      <CapacityIndicator
                        variant="capacity"
                        units={tier.units!}
                        maxUnits={tier.maxUnits!}
                        tierColor={tier.tierColor}
                        onDark={tier.onDark}
                        breakdown={tier.breakdown}
                        example={tier.example}
                      />
                    )}
                  </div>
                  <p
                    className={`text-sm mb-8 flex-grow font-['Roboto'] ${
                      tier.onDark ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {tier.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className={`flex items-start text-sm ${
                          tier.onDark ? 'text-gray-300' : 'text-gray-600'
                        }`}
                      >
                        <svg
                          className="w-5 h-5 text-primary mr-2 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )))}
            </div>

            <TShirtSizeGuide />
          </div>
        </section>

        <CTASection />
      </main>

      <Footer />
    </div>
  )
}